import React, { useState, useRef, useEffect } from 'react';

export default function ChatInterface({
  userLocation,
  locations,
  onMapFocus,
  onFilterLocations,
  filterPOIs,
  resolveTargetTaxonomy,
  detectWard
}) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'မင်္ဂလာပါဗျာ။ မြစ်ကြီးနားမြေပုံ Spatial AI Engine မှ ကြိုဆိုပါတယ်။ ဘယ်လုပ်ငန်းခွဲ သို့မဟုတ် ရပ်ကွက်ကို ရှာဖွေပေးရမလဲခင်ဗျာ။' }
  ]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return 9999;
    const R = 6371; 
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon/2) * Math.sin(dLon/2);
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isSending) return;

    const userText = input.trim();
    setMessages(prev => [...prev, { role: 'user', text: userText }]);
    setInput('');
    setIsSending(true);

    try {
      const taxonomyMeta = resolveTargetTaxonomy(userText);
      let pool = [...locations];

      if (taxonomyMeta) pool = filterPOIs(pool, taxonomyMeta);

      let results = pool.map(loc => ({
        ...loc,
        distance: calculateDistance(userLocation.lat, userLocation.lng, loc.lat, loc.lng)
      }));

      if (userText.includes("အဝေးဆုံး")) {
        results.sort((a, b) => b.distance - a.distance);
      } else {
        results.sort((a, b) => a.distance - b.distance);
      }

      let limit = 5;
      const countMatch = userText.match(/(\d+)\s*(ဆိုင်|ခု)/);
      if (countMatch) limit = parseInt(countMatch[1]);

      const finalResults = results.slice(0, limit);

      if (taxonomyMeta && taxonomyMeta.isHelp) {
        setMessages(prev => [...prev, { role: 'assistant', text: `🤖 **မြစ်ကြီးနား GIS AI လမ်းညွှန်ချက်**\n\nကျွန်တော့်ထံတွင် လုပ်ငန်းကြီး ၁၉ ခုနှင့် လုပ်ငန်းခွဲ ၁၃၄ ခု ရှိပါသည်။\n\n💡 **မေးမြန်းနိုင်သော ပုံစံများ -**\n• "အနီးဆုံး ဆေးဆိုင်ပြပါ"\n• "ွေလွဲ နိုင်တဲ့နေရာပြပါ"\n• "ကော်ဖီဆိုင် ၃ ဆိုင်ပြပါ"` }]);
      } else if (finalResults.length === 0) {
        setMessages(prev => [...prev, { role: 'assistant', text: `⚠️ ဝမ်းနည်းပါတယ်ခင်ဗျာ။ သင့်ရှာဖွေမှုအတွက် နီးစပ်သော မြေပုံအချက်အလက် မတွေ့ရှိပါ။` }]);
        onFilterLocations(locations);
      } else {
        onFilterLocations(finalResults);
        if (finalResults[0].lat && finalResults[0].lng) onMapFocus([finalResults[0].lat, finalResults[0].lng], 15);

        let responseText = `📍 ရှာဖွေမှုအတွက် အဆင်ပြေဆုံး ${finalResults.length} ခု တွေ့ရှိပါသည် -\n\n`;
        finalResults.forEach((loc, index) => {
          responseText += `${index + 1}. ${loc.name_mm || loc.name} (${loc.distance.toFixed(2)} km) ${loc.phone ? `- 📞 ${loc.phone}` : ''}\n`;
        });
        setMessages(prev => [...prev, { role: 'assistant', text: responseText }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: '❌ စနစ်အတွင်း အမှားတစ်ခု ဖြစ်ပွားသွားပါသည်။' }]);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="chat-interface-container" style={{ position: 'fixed', bottom: '80px', right: '20px', width: '380px', height: '450px', background: '#1e293b', borderRadius: '12px', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 20px rgba(0,0,0,0.4)', zIndex: 999, border: '1px solid #334155', color: '#f8fafc' }}>
      <div className="chat-header" style={{ padding: '12px', background: '#0f172a', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', fontWeight: 'bold', fontSize: '14px', borderBottom: '1px solid #334155' }}>🤖 MKA Spatial AI Engine v10.0 (Fuzzy Matching)</div>
      <div className="chat-messages" style={{ flex: 1, padding: '12px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', background: msg.role === 'user' ? '#3b82f6' : '#334155', padding: '8px 12px', borderRadius: '8px', maxWidth: '85%', whiteSpace: 'pre-line' }}>{msg.text}</div>
        ))}
        <div ref={chatEndRef} />
      </div>
      <form onSubmit={handleSend} style={{ display: 'flex', padding: '8px', borderTop: '1px solid #334155', background: '#0f172a', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
        <input value={input} onChange={e => setInput(e.target.value)} placeholder="အနီးဆုံး ဆေးဆိုင်ပြပါ..." style={{ flex: 1, padding: '8px 12px', background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', color: 'white', fontSize: '13px', outline: 'none' }} disabled={isSending} />
        <button type="submit" style={{ marginLeft: '6px', padding: '8px 14px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }} disabled={isSending}>{isSending ? '...' : 'ပို့ရန်'}</button>
      </form>
    </div>
  );
}